import React, { useState } from "react";
import { Stack, Paper, Typography, TextField, Button } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

const StatusCountCard = ({ label, count, isSelected, onClick }) => (
    <Paper
        elevation={3}
        onClick={onClick}
        sx={{
            cursor: "pointer",
            transition: "all 0.2s",
            backgroundColor: isSelected ? "#d1e0ff" : "white",
            border: isSelected ? "2px solid #3f51b5" : "1px solid #ccc",
            padding: "12px",
            width: "140px",
            height: "100px",
            minWidth: "140px",
            minHeight: "100px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
        }}
    >
        <Typography variant="subtitle2" color="textSecondary">
            {label}
        </Typography>
        <Typography variant="h6" fontWeight="bold">
            {count}
        </Typography>
    </Paper>
);

const AppointmentStatusCounts = ({ counts = {}, selectedStatus, onStatusChange, onFilterChange }) => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [filtered, setFiltered] = useState(false);
    const [dateError, setDateError] = useState("");

    // Calculate total count safely
    const calculateTotal = () => {
        if (!counts || typeof counts !== 'object') return 0;
        return Object.values(counts).reduce((a, b) => a + (b || 0), 0);
    };

    const statuses = [
        { key: "", label: "All", count: calculateTotal() },
        { key: "PENDING", label: "Pending", count: counts?.PENDING || 0 },
        { key: "CONFIRMED", label: "Confirmed", count: counts?.CONFIRMED || 0 },
        { key: "COMPLETED", label: "Completed", count: counts?.COMPLETED || 0 },
        { key: "CANCELLED", label: "Cancelled", count: counts?.CANCELLED || 0 },
        { key: "NO_SHOW", label: "No Show", count: counts?.NO_SHOW || 0 },
    ];

    const handleDateChange = (key, value) => {
        const formattedDate = value ? dayjs(value).format("YYYY-MM-DD") : null;
        if (key === "startDate") setStartDate(formattedDate);
        if (key === "endDate") setEndDate(formattedDate);
    };

    const handleSubmit = () => {
        setDateError("");
        if (startDate && endDate) {
            if (dayjs(endDate).isBefore(dayjs(startDate))) {
                setDateError("End date should not be less than start date.");
                return;
            }
            onFilterChange({ startDate, endDate, status: selectedStatus });
            setFiltered(true);
        } else {
            setDateError("Please select both start and end dates.");
        }
    };

    const handleClearFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setFiltered(false);
        // Call onFilterChange after state updates
        setTimeout(() => {
            onFilterChange({ startDate: "", endDate: "" , status: null });
        }, 0);
    };

    
    return (
        <Stack
            direction="column"
            spacing={3} // Adds space between sections
            sx={{ maxWidth: "100%", paddingY: 2 }}
        >
            {/* Status Cards Section */}
            <Stack direction="row" spacing={1.5} flexWrap="wrap">
                {statuses.map(({ key, label, count }) => (
                    <StatusCountCard
                        key={key}
                        label={label}
                        count={count}
                        isSelected={selectedStatus === key}
                        onClick={() => onStatusChange(key)}
                    />
                ))}
            </Stack>
    
            {/* Date Pickers Section */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ marginTop: 3 }}>
                <DatePicker
                    label="Start Date"
                    value={startDate ? dayjs(startDate) : null}
                    onChange={(newValue) => handleDateChange("startDate", newValue)}
                    slotProps={{ textField: { size: "small" } }}
                />
                <DatePicker
                    label="End Date"
                    value={endDate ? dayjs(endDate) : null}
                    onChange={(newValue) => handleDateChange("endDate", newValue)}
                    slotProps={{ textField: { size: "small" } }}
                />
            </Stack>
            {dateError && (
                <Typography color="error" variant="body2" sx={{ mt: -2, mb: 1 }}>
                    {dateError}
                </Typography>
            )}
    
            {/* Buttons Section */}
            <Stack direction="row" spacing={2}>
                <Button variant="contained" color="primary" onClick={handleSubmit} disabled={filtered}>
                    {filtered ? "Filters Applied" : "Submit"}
                </Button>
                <Button variant="outlined" color="secondary" onClick={handleClearFilters}>
                    Clear Filters
                </Button>
            </Stack>
        </Stack>
    );    
};

export default AppointmentStatusCounts;